import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faSave,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCriarFormaPagamento, useContasFormaPagamento } from 'hooks/useFormaPagamento';
import { CriarFormaPagamentoDto } from 'services/formaPagamentoService';
import { tiposFormaPagamento } from 'data/formasPagamento';
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useToast } from "providers/ToastProvider";
import SeletorPrazos from "components/prazo/SeletorPrazos";

// Define breadcrumbs for the new forma pagamento page
const formaPagamentoBreadcrumbItems = () => [
    {
        label: "Retaguarda",
        url: "#!",
    },
    {
        label: "Cadastros",
        url: "#!",
    },
    {
        label: "Formas de Pagamento",
        url: "/cadastros/formas-pagamento",
    },
    {
        label: "Nova",
        active: true,
    },
];

const NovaFormaPagamento: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Estado para prazos selecionados
    const [prazosIds, setPrazosIds] = useState<string[]>([]);

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<CriarFormaPagamentoDto>({
        defaultValues: {
            descricao: '',
            tipo: 0,
            contaDestinoId: undefined,
            movimentaCaixa: true,
        },
        mode: 'onSubmit',
    });

    // Hooks
    const criarMutation = useCriarFormaPagamento();
    const contasQuery = useContasFormaPagamento();

    const onSubmit = async (data: CriarFormaPagamentoDto) => {
        try {
            const payload = {
                ...data,
                contaDestinoId: data.contaDestinoId || undefined,
                prazosIds: prazosIds.length > 0 ? prazosIds : undefined,
            };
            await criarMutation.mutateAsync(payload);

            // Navigate with success message
            navigate('/cadastros/formas-pagamento');
            showToast('Forma de pagamento criada com sucesso', 'success');
        } catch (error) {
            console.error('Erro ao criar forma de pagamento:', error);
        }
    };

    return (
        <div>
            <PageBreadcrumb items={formaPagamentoBreadcrumbItems()} />

            <Form
                noValidate
                className="mb-9"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                    <div>
                        <h2 className="mb-2">Cadastrar uma Forma de Pagamento</h2>
                        <h5 className="text-body-tertiary fw-semibold">
                            Preencha as informações da nova forma de pagamento
                        </h5>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={criarMutation.isPending}
                        >
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            {criarMutation.isPending ? "Salvando..." : "Salvar Forma de Pagamento"}
                        </Button>
                    </div>
                </div>

                {/* Informações Básicas Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                            Informações Básicas
                        </h5>

                        {criarMutation.isError && (
                            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-4">
                                <div className="text-danger-emphasis">
                                    <strong>Erro:</strong> Não foi possível criar a forma de pagamento.
                                    Tente novamente.
                                </div>
                            </Alert>
                        )}

                        <div className="row g-3">
                            {/* Descrição */}
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Descrição *
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('descricao', {
                                            required: 'Descrição é obrigatória',
                                            maxLength: {
                                                value: 100,
                                                message: 'Descrição deve ter no máximo 100 caracteres'
                                            },
                                            validate: (value) => {
                                                if (!value?.trim()) {
                                                    return 'Descrição não pode estar vazia';
                                                }
                                                return true;
                                            }
                                        })}
                                        isInvalid={!!errors.descricao}
                                        placeholder="Ex: Dinheiro, Cartão de Crédito, PIX..."
                                        autoFocus
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.descricao?.message}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-body-secondary">
                                        Nome que identificará esta forma de pagamento
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Tipo */}
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Tipo *
                                    </Form.Label>
                                    <Form.Select
                                        {...register('tipo', {
                                            required: 'Tipo é obrigatório',
                                            setValueAs: (value) => value === '' ? undefined : Number(value)
                                        })}
                                        isInvalid={!!errors.tipo}
                                    >
                                        {tiposFormaPagamento.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>
                                                {tipo.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.tipo?.message}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-body-secondary">
                                        Categoria da forma de pagamento
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Movimenta Caixa */}
                            <div className="col-12">
                                <Form.Check
                                    type="switch"
                                    id="movimentaCaixa"
                                    label="Movimenta Caixa"
                                    {...register('movimentaCaixa')}
                                    defaultChecked
                                />
                                <Form.Text className="text-body-secondary">
                                    Quando ativo, pagamentos com esta forma geram movimento no caixa do PDV.
                                </Form.Text>
                            </div>

                            {/* Conta Destino */}
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Conta Destino (Opcional)
                                    </Form.Label>
                                    <Form.Select
                                        {...register('contaDestinoId', {
                                            setValueAs: (value) => value === '' ? undefined : value
                                        })}
                                        disabled={contasQuery.isLoading}
                                    >
                                        <option value="">Selecione uma conta (opcional)</option>
                                        {contasQuery.data?.map((conta: any) => (
                                            <option key={conta.id} value={conta.id}>
                                                {conta.nome}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {contasQuery.isLoading && (
                                        <Form.Text className="text-body-secondary">
                                            <Spinner size="sm" className="me-1" />
                                            Carregando contas...
                                        </Form.Text>
                                    )}
                                    {!contasQuery.isLoading && (
                                        <Form.Text className="text-body-secondary">
                                            Conta para onde o valor será depositado ao fechar o caixa.
                                            Para dinheiro, será usado também para sangrias e suprimentos.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Prazos Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            Prazos de Pagamento
                        </h5>

                        <SeletorPrazos
                            prazosIds={prazosIds}
                            onPrazosChange={setPrazosIds}
                            disabled={criarMutation.isPending}
                        />
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => navigate("/cadastros/formas-pagamento")}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={criarMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {criarMutation.isPending ? "Salvando..." : "Salvar Forma de Pagamento"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default NovaFormaPagamento; 